import React, { memo, useCallback, useState } from 'react';
import styled, { css } from 'styled-components';
import { useRouter } from 'next/compat/router';
import { observer } from 'mobx-react';
import _ from 'lodash';

import { useFlags } from 'launchdarkly-react-client-sdk';
import useColors from 'hooks/use-colors';
import usePaths from 'hooks/use-paths';
import useTranslation from 'hooks/use-translation';
import useUI from 'hooks/use-ui';
import useMenuFilterOptions from 'hooks/use-menu-filter-options';
import { useSpecialsNavVisibility } from 'src/dispensary/hooks/use-specials-nav-visibility';
import useDispensaryCategories from 'src/dispensary/hooks/use-dispensary-categories';
import { useDispensaryCategory } from 'src/hooks/use-dispensary-category';
import { useHideSalesLanguage } from 'src/hooks/use-hide-sales-language';

import MenuIcon from 'assets/menu-icon';
import { DesktopOnly, MobileOnly, Clickable } from 'shared/components';
import { Link } from 'components/core';
import { heading4 } from 'components/core/typography';
import CategoriesDropdown from 'components/categories-dropdown';
import ROUTES from 'src/routes';
import { tracker } from 'src/utils/analytics';
import { useHideDiscountFromConsumer } from 'shared/hooks/use-hide-discounts-from-consumer';
import MenuDrawer from './menu-drawer';

const { BRANDS, INFO, SPECIALS, DISPENSARY_CNAME } = ROUTES;

const TabLink = observer(({ path, text }) => {
  const { route } = useRouter() ?? {};
  const { href: baseHref, route: baseRoute } = usePaths();
  const { isDutchieMain } = useUI();
  const flags = useFlags();
  const activeColor = isDutchieMain && flags['growth.ecomm.homepage-v3.rollout'] ? `#4d2a3d` : undefined;

  const Anchor = isDutchieMain ? MainAnchor : EmbeddedAnchor;
  const tabHref = `${baseHref}${path}`;
  const tabRoute = `${baseRoute}${path}`;
  const isHome = path.length === 0;
  const isHomeActive = route === DISPENSARY_CNAME;
  const isActive = _.startsWith(route, tabRoute);
  const isSpecialsActive = path.includes('specials');
  const isBrands = path.includes('brands');

  const handleTabLinkAnalyticsTracking = () => {
    if (isSpecialsActive) {
      tracker.gaGTMClickEvent({
        eventCategory: `navigation click`,
        eventAction: `main menu`,
        eventLabel: `specials`,
      });
    }
    if (isBrands) {
      tracker.gaGTMClickEvent({
        eventAction: `main menu`,
        eventCategory: `navigation click`,
        eventLabel: `brands`,
      });
    }
    if (isHome) {
      tracker.gaGTMClickEvent({
        eventAction: `main menu`,
        eventCategory: `navigation click`,
        eventLabel: `home`,
      });
    }
  };

  return (
    <Link route={tabRoute} href={tabHref}>
      <Anchor
        active={isHome ? isHomeActive : isActive}
        href={tabHref}
        isDutchieMain={isDutchieMain}
        $activeColor={activeColor}
        onClick={handleTabLinkAnalyticsTracking}
      >
        {text}
      </Anchor>
    </Link>
  );
});

function NavLinks(props) {
  const { dispensary } = props;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const handleDrawerClose = useCallback(() => setDrawerOpen(false), []);
  const { isDutchieMain, showSearchInput } = useUI();
  const colors = useColors();
  const { t } = useTranslation();
  const { hasProductsOnSpecial } = useMenuFilterOptions({
    skipCurrentFilters: true,
  });
  const showSpecialsNav = useSpecialsNavVisibility(hasProductsOnSpecial);
  const hideDiscount = useHideDiscountFromConsumer(dispensary);
  const currentCategory = useDispensaryCategory();
  const { categories } = useDispensaryCategories();
  const HOME = '';

  const hideSalesLanguage = useHideSalesLanguage();
  const specialsText = hideSalesLanguage ? t('common.offer_plural', 'Offers') : t('common.special_plural', 'Specials');

  return (
    <>
      <MobileOnly>
        <MobileMenuContainer onClick={() => setDrawerOpen(true)}>
          <MenuIcon fill={colors.grey[75]} />
          {!showSearchInput && <MobileMenuLabel>{t('common.menu', 'Menu')}</MobileMenuLabel>}
        </MobileMenuContainer>
        <MenuDrawer dispensary={dispensary} open={drawerOpen} onClose={handleDrawerClose} />
      </MobileOnly>
      <DesktopOnly display={isDutchieMain ? 'contents' : 'initial'}>
        <Links>
          <TabLink path={HOME} text={t('common.home', 'Home')} />
          <CategoriesDropdown categories={categories} currentCategory={currentCategory} isDutchieMain={isDutchieMain} />
          <TabLink path={BRANDS} text={t('common.brand_plural', 'Brands')} />
          {showSpecialsNav && !hideDiscount && <TabLink path={SPECIALS} text={specialsText} />}
          {isDutchieMain && <TabLink path={INFO} text={t('common.info', 'Info')} />}
        </Links>
      </DesktopOnly>
    </>
  );
}

export default memo(observer(NavLinks));

const Links = styled.nav`
  display: flex;
  margin-left: -10px;
  height: 100%;
`;

const anchorStyles = css`
  ${heading4}

  cursor: pointer;
  font-size: 14px;
  padding: 10px;
  margin-right: 20px;

  color: var(--dcm-color-text-primary, ${({ theme }) => theme.colors.primaryBlack});
`;

const EmbeddedAnchor = styled.a`
  ${anchorStyles}
`;

const MainAnchor = styled.a`
  ${anchorStyles}

  line-height: 25px;
  padding-top: 19px;

  color: ${({ active, $activeColor, theme }) =>
    active ? $activeColor ?? theme.colors.primaryGreen : theme.colors.grey[30]};
  border-bottom: ${({ active, $activeColor, theme }) =>
    active ? `2px ${$activeColor ?? theme.colors.primaryGreen} solid` : `none`};
`;

/* mobile menu */
const MobileMenuContainer = styled(Clickable)`
  display: flex;
  flex: 1 auto;
  height: 20px;

  svg {
    cursor: pointer;
  }
`;

const MobileMenuLabel = styled.span`
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  margin-left: 15px;

  color: ${({ theme }) => theme.colors.grey[55]};
`;
